<script setup lang="ts">
import { useField, useForm } from 'vee-validate';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import {
  GrammarFooter,
  GrammarModal,
  GrammarPrimaryButton,
  GrammarSecondaryButton,
} from '@nora-monorepo/grammar-ui';

import DateTimePicker from '#ui-shared/components/date-time-picker/DateTimePicker.vue';

interface CustomNotificationsModalProps {
  modalActive?: boolean;
  initialDate?: Date | null;
  headerTitle?: string;
  onCancel: () => void;
  onConfirm: (date: Date) => void;
}

const props = defineProps<CustomNotificationsModalProps>();
const { t } = useI18n();
const { modalActive, initialDate, headerTitle, onConfirm, onCancel }
  = toRefs(props);

const schema = yup.object({
  selectedDateTime: yup.date().min(new Date(), 'Date needs to be in the future'),
});

const { errors, validate } = useForm({
  validationSchema: schema,
  initialValues: {
    selectedDateTime: initialDate?.value ? new Date(initialDate.value) : new Date(),
  },
});

const { value: selectedDateTime } = useField<Date>('selectedDateTime');

async function confirm() {
  if (selectedDateTime.value) {
    const { valid } = await validate();

    if (!valid) {
      return;
    }
    onConfirm.value(selectedDateTime.value);
  }
}

const shouldDisableConfirmButton = computed(() => {
  return !selectedDateTime.value || !!errors.value.selectedDateTime;
});
</script>

<template>
  <GrammarModal :is-open="modalActive" :label="headerTitle" @close="onCancel">
    <template #content>
      <DateTimePicker
        :selected-date-time="selectedDateTime"
        :min-date="new Date()"
        :errors="errors.selectedDateTime"
        @update="selectedDateTime = $event"
      />
    </template>
    <template #footer>
      <GrammarFooter>
        <template #left-button>
          <GrammarSecondaryButton
            :label="t('common.cancel')"
            @click="onCancel"
          />
        </template>
        <template #right-button>
          <GrammarPrimaryButton
            :disabled="shouldDisableConfirmButton"
            :label="t(`common.confirm`)"
            @click="confirm"
          />
        </template>
      </GrammarFooter>
    </template>
  </GrammarModal>
</template>
